import CharacterCount from '@boViews/shared/CharacterCount';
import LanguageTabs from '@boViews/shared/LanguageTabs';
import { formatList, languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { faArrowRotateLeft, faCopy, faLightbulb } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import InputText from '@sharedComponents/InputText';
import InputTextArea from '@sharedComponents/InputTextArea';
import Typography from '@sharedComponents/Typography';
import { compactI18n, parseI18n, REFERENCE_LANGUAGE } from '@src/i18n';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DESCRIPTION_MAX_LENGTH, emptyLanguages, NAME_MAX_LENGTH } from '../collectionForm';
import { appliesTo, moreSpecificRows, targetOf, toOverrideText } from '../overrideRules';

import * as style from './style.module.css';

// Sentence telling which platforms the row reaches.
const scopeSentence = (t, scope) => {
    if (scope.kind === 'platform') {
        return t('Applies only to the platform {{url}} ({{client}}).', { url: scope.url, client: scope.clientName });
    }
    if (scope.kind === 'parent') {
        return t('Applies to the {{count}} platform(s) of {{name}} and of its {{subClients}}.', {
            count: scope.platformCount,
            name: scope.name,
            subClients: t('{{count}} sub-client(s)', { count: scope.subClientCount }),
        });
    }
    return t('Applies to the {{count}} platform(s) of {{name}}.', { count: scope.platformCount, name: scope.name });
};

// Label of the text of the collection, followed by the name of the active language outside the reference one.
const collectionLabel = (t, isTitle, language) => {
    if (isTitle) return language ? t('Title of the collection · {{language}}', { language }) : t('Title of the collection');
    return language ? t('Description of the collection · {{language}}', { language }) : t('Description of the collection');
};

// Whether two texts keyed by language hold the same filled languages, with the same values.
const sameTexts = (a, b) => {
    const filledA = compactI18n(a);
    const filledB = compactI18n(b);
    const languages = Object.keys(filledA);
    return languages.length === Object.keys(filledB).length && languages.every((language) => filledA[language] === filledB[language]);
};

/**
 * Body of the modal editing the overridden `field` ('title' or 'description') of an override `row`, language
 * by language. It tells what the row applies to, excluding the targets of `overrides` that have their own row,
 * and shows the text of the `collection` that an empty language falls back to. `onSave(text)` receives the
 * i18n JSON string of the filled languages, or null, and resolves to whether it was saved: once saved, the
 * modal closes itself through `onClose`, its buttons left disabled as it stays mounted while it fades out;
 * otherwise they are enabled again. A save that settles once this body is unmounted, another modal having
 * replaced it, does nothing.
 */
const OverrideTextModal = ({
    field,
    row,
    overrides = [],
    collection,
    languages = [],
    onSave = async () => false,
    onCancel = () => {},
    onClose = () => {},
}) => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const isTitle = field === 'title';
    const [texts, setTexts] = useState(() => ({
        ...Object.fromEntries(languages.map(({ label }) => [label, ''])),
        ...parseI18n(row[field]),
    }));
    const [active, setActive] = useState(REFERENCE_LANGUAGE);
    // Button whose save is in progress ('save' or 'reset'), or null.
    const [saving, setSaving] = useState(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const targetName = targetOf(row).name;
    const excluded = moreSpecificRows(row, overrides).map((other) => targetOf(other).name);
    const sentences = [
        scopeSentence(t, appliesTo(row)),
        excluded.length > 0 &&
            t('Except {{targets}}, which have their own override.', {
                count: excluded.length,
                targets: formatList(excluded, locale),
            }),
        isTitle
            ? t('A language left empty (orange dot) shows the title of the collection in this language.')
            : t('A language left empty (orange dot) shows the description of the collection in this language.'),
    ].filter(Boolean);

    const text = texts[active] ?? '';
    const activeName = languageName(active, locale);
    const collectionText = parseI18n(isTitle ? collection?.name : collection?.description)[active] ?? '';
    const busy = saving !== null;

    const changeText = (value) => setTexts((current) => ({ ...current, [active]: value }));

    const submit = async (value, button) => {
        setSaving(button);
        const saved = await onSave(value);
        if (!mounted.current) return;
        if (saved) onClose();
        else setSaving(null);
    };

    const fieldProps = {
        'aria-label': isTitle ? t('Title for {{name}}', { name: targetName }) : t('Description for {{name}}', { name: targetName }),
        placeholder: collectionText,
        value: text,
        onChange: (event) => changeText(event.target.value),
    };

    return (
        <FlexBox flexDirection="column" gap="m">
            <Typography fontSize="s" color="neutral-secondary">
                {sentences.join(' ')}
            </Typography>
            <FlexBox flexDirection="column" className={style.languageBox}>
                <LanguageTabs
                    languages={languages}
                    active={active}
                    emptyLanguages={emptyLanguages(texts, languages)}
                    referenceLanguage={REFERENCE_LANGUAGE}
                    onChange={setActive}
                />
                <FlexBox flexDirection="column" gap="xs" className={style.fields}>
                    <FlexBox justifyContent="space-between" alignItems="center" gap="xs" flexWrap="wrap">
                        <Typography fontSize="s" color="neutral-tertiary">
                            {collectionLabel(t, isTitle, active === REFERENCE_LANGUAGE ? null : activeName)}
                        </Typography>
                        <Button
                            ghost
                            icon={faCopy}
                            label={t('Reuse this text')}
                            disabled={!collectionText}
                            action={() => changeText(collectionText)}
                        />
                    </FlexBox>
                    <Typography color={collectionText ? 'neutral-secondary' : 'neutral-tertiary'}>
                        {collectionText || t('No text in this language.')}
                    </Typography>
                    {isTitle ? <InputText {...fieldProps} /> : <InputTextArea {...fieldProps} />}
                    <CharacterCount length={text.length} max={isTitle ? NAME_MAX_LENGTH : DESCRIPTION_MAX_LENGTH} />
                    {!text.trim() && (
                        <InformationMessage
                            icon={faLightbulb}
                            text={t('Empty field: the translation of the collection for {{language}} will be used.', {
                                language: activeName,
                            })}
                        />
                    )}
                </FlexBox>
            </FlexBox>
            <FlexBox justifyContent="space-between" alignItems="center" gap="s" flexWrap="wrap" className={style.footer}>
                <Button
                    ghost
                    theme="danger"
                    icon={faArrowRotateLeft}
                    label={isTitle ? t('Back to the title of the collection') : t('Back to the description of the collection')}
                    disabled={busy || !row[field]}
                    isLoading={saving === 'reset'}
                    action={() => {
                        submit(null, 'reset');
                    }}
                />
                <FlexBox gap="s" flexWrap="wrap">
                    <Button outline label={t('Cancel')} disabled={busy} action={onCancel} />
                    <Button
                        label={t('Save')}
                        disabled={busy || sameTexts(texts, parseI18n(row[field]))}
                        isLoading={saving === 'save'}
                        action={() => {
                            submit(toOverrideText(texts), 'save');
                        }}
                    />
                </FlexBox>
            </FlexBox>
        </FlexBox>
    );
};

export default OverrideTextModal;
